<template>
	<div class="survey">
		<div class="survey-container">
			<div class="d-flex justify-space-between mb-7">
				<div>
					<h2 class="mr-5">Enkät om resvanor</h2>
					<p>– tack för att du bidrar!</p>
				</div>
				<div style="text-align: right; " v-if="endUser && endUser.client && endUser.client.logo">
          <img
            v-if="endUser && endUser.client && endUser.client.logo"
            width="200"
            :src="apiUrl + '/storage/' + endUser.client.logo"
            alt=""
          />
				</div>
			</div>

			<div v-if="stepIndex === 0">
				<p>I denna enkät får du svara på hur ditt vardagsresande ser ut idag och hur det ändrats sista tiden. Dina svar är värdefulla – oavsett om du färdas långt, kort, ofta eller sällan. Vi använder dem för att skapa ännu bättre förutsättningar för smart resande åt dig och oss alla.
				<br/>Läs mer om <a style="text-decoration: none;" href="https://goteborg.se/resvana" target="_blank">bakgrunden till enkäten och din integritet</a>.</p>
				<p>Det går snabbt att svara.</p>

				<h2 class="mt-10 mb-2">Tänk på ditt resande <span style="color: #00A9CE;">de senaste veckorna</span>. I genomsnitt, hur många enkelresor gör du en vanlig vecka?</h2>
				<p class="mb-0"><em class="smaller-em">Fundera på vad som känns rimligt och fyll i det du tror.</em></p>


				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('mainModeOfTransport')}">Vad anser du vara ditt huvudsakliga färdsätt?</h4>

				<ModeOfTransport :key="1" :value="mainModeOfTransport" @selected="selectModeOfTransport" />

				<div class="nudgd-btn inline survey-submit mt-5" @click="confirmFirstStep()" v-if=" ! firstStepConfirmed">Gå vidare</div>
				<div style="clear:both;"></div>

				<template v-if="firstStepConfirmed">

					<h4 class="mt-2 mb-3">Hur många dagar per vecka använder du följande färdsätt, i genomsnitt <span style="color: #00A9CE;">de senaste veckorna</span>?</h4>
					<p class="mb-2"><em class="smaller-em">(Runda av till närmaste heltal. Så om du bara använder ett visst färdsätt en gång per månad blir det 1/4 = 0,25 –> 0. Vänligen svara på alla.)</em></p>
					<div class="survey-frequency-chips walk">
						<h3 :class="{surveyError: errors.includes('frequencyWalk')}">🚶 Till fots</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip walking" :class="{active: frequencyWalk == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyWalk', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyBike')}">🚲 Cykel</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip bike" :class="{active: frequencyBike == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyBike', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyTransit')}">🚌 Kollektivt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip transit" :class="{active: frequencyTransit == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyTransit', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCar')}">🚗 Bil - själv/med hushållsmedlem</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCar == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyCar', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyCarCoride')}">🚙 Bil - samåkning med annan</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip car" :class="{active: frequencyCarCoride == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyCarCoride', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>
					<div class="survey-frequency-chips">
						<h3 :class="{surveyError: errors.includes('frequencyMisc')}">Övrigt</h3>
						<div class="survey-frequency-chips-container">
							<div class="survey-frequency-chips-container-chip" :class="{active: frequencyMisc == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('frequencyMisc', number)">{{ number }}</div>
						</div>
						<div class="survey-frequency-chips-helpers right">
							<div>dagar per vecka</div>
						</div>
					</div>


					<h4 class="mt-2 mb-1">Hur många gånger per vecka har du färdats, i genomsnitt <span style="color: #00A9CE;">de senaste veckorna</span>?</h4>
					<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor.)</em></p>

					<div class="survey-thirds mt-3">
						<div class="survey-thirds-first" :class="{surveyError: errors.includes('numberOfTrips')}">Antal resor:</div>

						<div class="survey-thirds-middle">
							<vue-slider dotSize="20" :max="35" v-model="numberOfTrips"></vue-slider>
						</div>

						<div class="survey-thirds-last"><v-text-field hide-details v-model="numberOfTrips"></v-text-field> st</div>
					</div>



					<h4 class="mt-6 mb-6">Hur långt har du i genomsnitt färdats per vecka med följande färdsätt?</h4>
<!--						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen dit och vägen tillbaka.)</em></p> -->
					<div class="survey-thirds walking mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyWalking')}">Till fots</div>
						<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyWalking"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyWalking"></v-text-field> km</div>
					</div>

					<div class="survey-thirds bike mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyBike')}">Cykel</div>
						<div class="survey-thirds-middle"><vue-slider :max="200" v-model="weeklyBike"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyBike"></v-text-field> km</div>
					</div>

					<div class="survey-thirds transit mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyTransit')}">Kollektivt</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyTransit"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyTransit"></v-text-field> km</div>
					</div>

					<div class="survey-thirds car mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCar')}">Bil - själv/med hushållsmedlem</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCar"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyCar"></v-text-field> km</div>
					</div>

					<div class="survey-thirds car carCoride mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyCarCoride')}">Bil - samåkning med annan</div>
						<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="weeklyCarCoride"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyCarCoride"></v-text-field> km</div>
					</div>

					<div class="survey-thirds misc mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyMisc')}">
							<v-tooltip bottom>
						      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Övrigt <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
						      <span>t.ex. elsparkcykel, skateboard etc."</span>
						    </v-tooltip>
						</div>
						<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="weeklyMisc"></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyMisc"></v-text-field> km</div>
					</div>

					<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ weeklySum }} km</span></div>

					<div class="survey-sum mt-6">
						Om du färdas enligt ovan under hela året blir det <strong>{{ yearlyDistance }} km</strong>.
						<br/>
						<br/>
						<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


					<div v-if="weeklyCar">
						<h4 class="mt-5">Hur är dina bilresor fördelade utifrån drivmedel?</h4>
						<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv/med hushållsmedlem ovan ({{ weeklyCar }} km))</em></p>

						<div class="survey-thirds electricity mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyElectricity')}">El</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyElectricity"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyElectricity"></v-text-field> km</div>
						</div>

						<div class="survey-thirds hybrid mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyHybrid')}">Hybrid/gas</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyHybrid"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyHybrid"></v-text-field> km</div>
						</div>

						<div class="survey-thirds gas mt-3">
							<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('weeklyGas')}">Bensin/diesel<br/>/etanol</div>
							<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="weeklyGas"></vue-slider></div>
							<div class="survey-thirds-last"><v-text-field hide-details v-model="weeklyGas"></v-text-field> km</div>
						</div>
					</div>

					<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
					<div style="clear:both;"></div>
				</template>
			</div>

			<div v-if="stepIndex === 1">
				<h2 class="mt-10 mb-2">Tänk på ditt resande <span style="color: #00A9CE;">förra våren</span>. I genomsnitt, hur många enkelresor gjorde du en vanlig vecka?</h2>
				<p class="mb-0"><em class="smaller-em">Fundera på vad som känns rimligt och fyll i det du tror.</em></p>

				<h4 class="mt-3 mb-3" :class="{surveyError: errors.includes('previousMainModeOfTransport')}">Vad anser du att ditt huvudsakliga färdsätt var för dina resor <span style="color: #00A9CE;">förra våren</span>?</h4>
				<ModeOfTransport :value="previousMainModeOfTransport" :key="2" @selected="selectpreviousModeOfTransport" />

				<h4 class="mt-2 mb-3">Hur många dagar per vecka använde du följande färdsätt, i genomsnitt <span style="color: #00A9CE;">förra våren</span>?</h4>
				<p class="mb-2"><em class="smaller-em">(Runda av till närmaste heltal. Så om du bara använder ett visst färdsätt en gång per månad blir det 1/4 = 0,25 –> 0. Vänligen svara på alla.)</em></p>
				<div class="survey-frequency-chips walk">
					<h3 :class="{surveyError: errors.includes('previousFrequencyWalk')}">🚶 Till fots</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip walking" :class="{active: previousFrequencyWalk == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyWalk', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyBike')}">🚲 Cykel</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip bike" :class="{active: previousFrequencyBike == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyBike', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyTransit')}">🚌 Kollektivt</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip transit" :class="{active: previousFrequencyTransit == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyTransit', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyCar')}">🚗 Bil - själv/med hushållsmedlem</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: previousFrequencyCar == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyCar', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyCarCoride')}">🚙 Bil - samåkning med annan</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip car" :class="{active: previousFrequencyCarCoride == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyCarCoride', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>
				<div class="survey-frequency-chips">
					<h3 :class="{surveyError: errors.includes('previousFrequencyMisc')}">Övrigt</h3>
					<div class="survey-frequency-chips-container">
						<div class="survey-frequency-chips-container-chip" :class="{active: previousFrequencyMisc == number}" v-for="number in [0, 1, 2, 3, 4, 5, 6, 7]" :key="number" @click="selectFrequency('previousFrequencyMisc', number)">{{ number }}</div>
					</div>
					<div class="survey-frequency-chips-helpers right">
						<div>dagar per vecka</div>
					</div>
				</div>

				<h4 class="mt-2 mb-1">Hur många gånger per vecka har du färdats, i genomsnitt <span style="color: #00A9CE;">förra våren</span>?</h4>
				<p class="mb-2"><em class="smaller-em">(Tur och retur räknas som två enkelresor.)</em></p>

				<div class="survey-thirds mt-3">
					<div class="survey-thirds-first" :class="{surveyError: errors.includes('previousNumberOfTrips')}">Antal resor:</div>

					<div class="survey-thirds-middle">
						<vue-slider dotSize="20" :max="35" v-model="previousNumberOfTrips"></vue-slider>
					</div>

					<div class="survey-thirds-last"><v-text-field hide-details placeholder="0" v-model="previousNumberOfTrips"></v-text-field> st</div>
				</div>


				<h4 class="mt-6 mb-6">Hur långt färdades du i genomsnitt per vecka med följande färdsätt, <span style="color: #00A9CE;">förra våren</span>?</h4>
<!--						<p class="mb-2"><em class="smaller-em">(Räkna med både vägen dit och vägen tillbaka.)</em></p> -->
				<div class="survey-thirds walking mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyWalking')}">Till fots</div>
					<div class="survey-thirds-middle"><vue-slider :max="200" v-model="previousWeeklyWalking"><template v-slot:dot><div class="custom-dot alt">🚶</div></template></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyWalking"></v-text-field> km</div>
				</div>

				<div class="survey-thirds bike mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyBike')}">Cykel</div>
					<div class="survey-thirds-middle"><vue-slider :max="200" v-model="previousWeeklyBike"><template v-slot:dot><div class="custom-dot alt">🚲</div></template></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyBike"></v-text-field> km</div>
				</div>

				<div class="survey-thirds transit mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyTransit')}">Kollektivt</div>
					<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyTransit"><template v-slot:dot><div class="custom-dot alt">🚌</div></template></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyTransit"></v-text-field> km</div>
				</div>

				<div class="survey-thirds car mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyCar')}">Bil - själv/med hushållsmedlem</div>
					<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyCar"><template v-slot:dot><div class="custom-dot alt">🚗</div></template></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyCar"></v-text-field> km</div>
				</div>

				<div class="survey-thirds car carCoride mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyCarCoride')}">Bil - samåkning med annan</div>
					<div class="survey-thirds-middle"><vue-slider :max="1000" v-model="previousWeeklyCarCoride"><template v-slot:dot><div class="custom-dot alt">🚙</div></template></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyCarCoride"></v-text-field> km</div>
				</div>

				<div class="survey-thirds misc mt-3">
					<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyMisc')}">
						<v-tooltip bottom>
					      <template v-slot:activator="{ on, attrs }"><span v-bind="attrs" v-on="on">Övrigt <div class="tooltip-container"><span class="icon-info"></span></div></span></template>
					      <span>t.ex. elsparkcykel, skateboard etc."</span>
					    </v-tooltip>
					</div>
					<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="200" v-model="previousWeeklyMisc"></vue-slider></div>
					<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyMisc"></v-text-field> km</div>
				</div>

				<div class="survey-thirds-sum"><strong>SUMMA</strong> <span>{{ previousWeeklySum }} km</span></div>

				<div class="survey-sum mt-6">
					Om du färdas enligt ovan under hela året blir det <strong>{{ previousYearlyDistance }} km</strong>.
					<br/>
					<br/>
					<em>Om reslängden per vecka verkar rimlig, vänligen gå vidare i enkäten. Om inte, justera i frågorna ovan.</em></div>


				<div v-if="previousWeeklyCar">
					<h4 class="mt-5">Hur var dina bilresor fördelade utifrån drivmedel?</h4>
					<p class="mb-2"><em class="smaller-em">(Summan för de olika drivmedlen måste matcha med Bil - själv/med hushållsmedlem ovan ({{ previousWeeklyCar }} km))</em></p>

					<div class="survey-thirds electricity mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyElectricity')}">El</div>
						<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyElectricity"></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyElectricity"></v-text-field> km</div>
					</div>

					<div class="survey-thirds hybrid mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyHybrid')}">Hybrid/gas</div>
						<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyHybrid"></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyHybrid"></v-text-field> km</div>
					</div>

					<div class="survey-thirds gas mt-3">
						<div class="survey-thirds-first-alt" :class="{surveyError: errors.includes('previousWeeklyGas')}">Bensin/diesel<br/>/etanol</div>
						<div class="survey-thirds-middle"><vue-slider dotSize="20" :max="1000" v-model="previousWeeklyGas"></vue-slider></div>
						<div class="survey-thirds-last"><v-text-field hide-details v-model="previousWeeklyGas"></v-text-field> km</div>
					</div>
				</div>

				<div class="nudgd-btn inline survey-submit mt-10" @click="nextStep()">Gå vidare</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<div v-if="stepIndex === 2">
				<h4 class="mt-2 mb-4" :class="{surveyError: errors.includes('usedResvana')}">Du fick för ett tag sen en länk till en tjänst kallad Resvana. Den innehåller bland annat info om olika hållbara transportalternativ nära dig och tips om att starta nya resvanor. Läste du något där inne eller använde kartan etc.?</h4>

				<div class="survey-radio-row mb-6">
					<v-radio-group class="mt-0" v-model="usedResvana" row>
						<v-radio label="Ja" value="Ja"></v-radio>
						<v-radio label="Nej" value="Nej"></v-radio>
					</v-radio-group>
				</div>

				<div v-if="usedResvana === 'Ja'">
					<h2>Tjänsten Resvana</h2>
					<h4 class="mt-4" :class="{surveyError: errors.includes('resvanaEffect')}">I vilken utsträckning skulle du säga att Resvana har bidragit till ditt val av färdmedel?</h4>
					<em class="smaller-em">(0 = Inte alls; 10 = Helt och hållet)</em>
					<vue-slider marks class="mt-3" :max="10" v-model="resvanaEffect"></vue-slider>


					<h4 class="mt-10 mb-4">Tjänsten har fått mig att börja...</h4>

					<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreWalking')}">...promenerera (eller åka rullstol) mer</div>
						<v-radio-group class="mt-0" v-model="more.walking" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Planerar att göra det" value="Planerar att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
							<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreBiking')}">...cykla mer</div>
						<v-radio-group v-model="more.biking" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Planerar att göra det" value="Planerar att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
							<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreTransit')}">...åka mer kollektivtrafik (av någon typ)</div>
						<v-radio-group v-model="more.transit" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Planerar att göra det" value="Planerar att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

						<div class="survey-radio-row">
						<div class="survey-radio-row-first" :class="{surveyError: errors.includes('moreCar')}">...åka bil på ett mer hållbart sätt, t.ex. elbil, samåkning, bilpool, etc.</div>
						<v-radio-group v-model="more.car" row>
									<v-radio label="Ja" value="Ja"></v-radio>
									<v-radio label="Planerar att göra det" value="Planerar att göra det"></v-radio>
									<v-radio label="Nej" value="Nej"></v-radio>
							</v-radio-group>
						</div>

					<h4 class="mb-3 mt-10">Kommentar <span style="font-weight:normal;">(ej obligatorisk)</span></h4>
					<v-textarea rows="4" hide-details solo v-model="commentary"></v-textarea>
					<div class="survey-help mt-2">(Vänligen lämna inga personuppgifter i detta fält)</div>

					<h4 class="mb-3 mt-6" :class="{surveyError: errors.includes('resvanaRecommend')}">Hur troligt är det att du skulle rekommendera Resvana till en kollega eller vän?</h4>
					<vue-slider marks class="pb-10" :max="10" v-model="resvanaRecommend"></vue-slider>
				</div>

				<h4 class="mt-2 mb-3" :class="{surveyError: errors.includes('usedResvana')}">Vi vill arbeta ihop med dig i vårt arbete med att göra samhällets resande hållbart. Får vi därför framöver be dig om råd och rekommendationer och fråga mer om dina resvanor? Stort tack i så fall! Ange i så fall din epost nedan.</h4>
			  <p style="font-size:13px;">Läs mer om <a style="font-weight: 600; text-decoration: none;" href="https://nudgd.se/om-nudgd/integritetspolicy/" target="_blank">hur vi hanterar personuppgifter</a></p>

			  <v-text-field dense hide-details placeholder="E-post" v-model="contactMe" outlined></v-text-field>

				<div class="nudgd-btn inline survey-submit mt-10" @click="submitResponse()">Skicka in</div>
				<div class="nudgd-btn small survey-submit mt-10" @click="prevStep()">Tillbaka</div>
				<div style="clear:both;"></div>
			</div>

			<v-progress-linear color="#00A19A" height="10" rounded class="mt-8" :value="(stepIndex + 1) / steps * 100"></v-progress-linear>
		</div>
	</div>
</template>

<script>
import axios from 'axios'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import ModeOfTransport from '@/components/ModeOfTransport.vue'

export default {
	components: {VueSlider, ModeOfTransport},

	mounted() {
		this.uuid = Math.random().toString(36).substr(2, 5)
		this.$store.state.surveyId = 10

		// For testing purposes
		if (this.$route.query.page) {
			this.stepIndex = parseInt(this.$route.query.page)
		}

		// Get end user
		axios.get(this.$apiUrl + '/api/end-user/' + this.slug).then(res => {
			this.$store.state.endUser = res.data.endUser
			this.$store.state.translations = res.data.translations
		})
	},

	data() {
		return {
			steps: 3,
			mainModeOfTransport: null,
			frequencyWalk: null,
			frequencyBike: null,
			frequencyTransit: null,
			frequencyCar: null,
			frequencyCarCoride: null,
			frequencyMisc: null,
			numberOfTrips: 0,

			weeklyWalking: 0,
			weeklyBike: 0,
			weeklyTransit: 0,
			weeklyCar: 0,
			weeklyCarCoride: 0,
			weeklyMisc: 0,
			weeklyElectricity: 0,
			weeklyHybrid: 0,
			weeklyGas: 0,

			previousMainModeOfTransport: null,
			previousFrequencyWalk: null,
			previousFrequencyBike: null,
			previousFrequencyTransit: null,
			previousFrequencyCar: null,
			previousFrequencyCarCoride: null,
			previousFrequencyMisc: null,
			previousNumberOfTrips: 0,

			previousWeeklyWalking: 0,
			previousWeeklyBike: 0,
			previousWeeklyTransit: 0,
			previousWeeklyCar: 0,
			previousWeeklyCarCoride: 0,
			previousWeeklyMisc: 0,

			previousWeeklyElectricity: 0,
			previousWeeklyHybrid: 0,
			previousWeeklyGas: 0,

			usedResvana: null,
			resvanaEffect: null,

			more: {
				walking: null,
				biking: null,
				transit: null,
				car: null
			},

			commentary: null,
			resvanaRecommend: null,
			contactMe: null,

			firstStepConfirmed: false,
			errors: [],
			engaged: [],
			stepIndex: 0,

			// Each array represents the required fields of a step
			required: [
				[
					'numberOfTrips', 'mainModeOfTransport', 'frequencyCar', 'frequencyCarCoride',
					'frequencyBike', 'frequencyWalk', 'frequencyTransit', 'frequencyMisc',
				],
				[
					'previousNumberOfTrips', 'previousMainModeOfTransport', 'previousFrequencyCar', 'previousFrequencyCarCoride',
					'previousFrequencyBike', 'previousFrequencyWalk', 'previousFrequencyTransit', 'previousFrequencyMisc',
				],
				[
					'usedResvana'
				],
			],
		}
	},

	watch: {
		previousNumberOfTrips(val) { this.engaged.push('previousNumberOfTrips') },
		previousMainModeOfTransport(val) { this.engaged.push('previousMainModeOfTransport') },
		previousFrequencyCar(val) { this.engaged.push('previousFrequencyCar') },
		previousFrequencyCarCoride(val) { this.engaged.push('previousFrequencyCarCoride') },
		previousFrequencyBike(val) { this.engaged.push('previousFrequencyBike') },
		previousFrequencyWalk(val) { this.engaged.push('previousFrequencyWalk') },
		previousFrequencyTransit(val) { this.engaged.push('previousFrequencyTransit') },
		previousFrequencyMisc(val) { this.engaged.push('previousFrequencyMisc') },

		numberOfTrips(val) { this.engaged.push('numberOfTrips') },
		mainModeOfTransport(val) { this.engaged.push('mainModeOfTransport') },
		frequencyCar(val) { this.engaged.push('frequencyCar') },
		frequencyCarCoride(val) { this.engaged.push('frequencyCarCoride') },
		frequencyBike(val) { this.engaged.push('frequencyBike') },
		frequencyWalk(val) { this.engaged.push('frequencyWalk') },
		frequencyTransit(val) { this.engaged.push('frequencyTransit') },
		frequencyMisc(val) { this.engaged.push('frequencyMisc') },

		weeklyCar(val) { this.engaged.push('weeklyCar') },
		weeklyWalking(val) { this.engaged.push('weeklyWalking') },
		weeklyBike(val) { this.engaged.push('weeklyBike') },
		weeklyTransit(val) { this.engaged.push('weeklyTransit') },
		weeklyMisc(val) { this.engaged.push('weeklyMisc') },
		weeklyCarCoride(val) { this.engaged.push('weeklyCarCoride') },
		weeklyElectricity(val) { this.engaged.push('weeklyElectricity') },
		weeklyGas(val) { this.engaged.push('weeklyGas') },
		weeklyHybrid(val) { this.engaged.push('weeklyHybrid') },

		previousWeeklyCar(val) { this.engaged.push('previousWeeklyCar') },
		previousWeeklyWalking(val) { this.engaged.push('previousWeeklyWalking') },
		previousWeeklyBike(val) { this.engaged.push('previousWeeklyBike') },
		previousWeeklyTransit(val) { this.engaged.push('previousWeeklyTransit') },
		previousWeeklyMisc(val) { this.engaged.push('previousWeeklyMisc') },
		previousWeeklyCarCoride(val) { this.engaged.push('previousWeeklyCarCoride') },
		previousWeeklyElectricity(val) { this.engaged.push('previousWeeklyCarCoride') },
		previousWeeklyGas(val) { this.engaged.push('previousWeeklyCarCoride') },
		previousWeeklyHybrid(val) { this.engaged.push('previousWeeklyCarCoride') },

		usedResvana(val) { this.engaged.push('usedResvana') },
		resvanaEffect(val) { this.engaged.push('resvanaEffect') },
		resvanaRecommend(val) { this.engaged.push('resvanaRecommend') },
		'more.walking': function (val) { this.engaged.push('moreWalking') },
		'more.biking': function (val) { this.engaged.push('moreBiking') },
		'more.transit': function (val) { this.engaged.push('moreTransit') },
		'more.car': function (val) { this.engaged.push('moreCar') },
	},

	methods: {
		selectFrequency(property, value) {
			this[property] = value
		},

		selectModeOfTransport(mode) {
			this.mainModeOfTransport = mode
		},

		selectpreviousModeOfTransport(mode) {
			this.previousMainModeOfTransport = mode
		},

		confirmFirstStep() {
			if ( ! this.mainModeOfTransport) {
				alert('Var god och välj ditt huvudsakliga färdsätt')
				return
			}

			this.firstStepConfirmed = true
		},

		nextStep() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			window.scrollTo(0, 0)
			this.stepIndex++
			this.saveState()
		},

		validate() {
			let required = this.required[this.stepIndex]

			if (this.stepIndex === 0) {
				if (parseInt(this.frequencyCar) > 0) { required.push('weeklyCar') }
				if (parseInt(this.frequencyCarCoride) > 0) { required.push('weeklyCarCoride') }
				if (parseInt(this.frequencyWalk) > 0) { required.push('weeklyWalking') }
				if (parseInt(this.frequencyBike) > 0) { required.push('weeklyBike') }
				if (parseInt(this.frequencyTransit) > 0) { required.push('weeklyTransit') }
				if (parseInt(this.frequencyMisc) > 0) { required.push('weeklyMisc') }
			}

			if (this.stepIndex === 1) {
				if (parseInt(this.previousFrequencyCar) > 0) { required.push('previousWeeklyCar') }
				if (parseInt(this.previousFrequencyCarCoride) > 0) { required.push('previousWeeklyCarCoride') }
				if (parseInt(this.previousFrequencyWalk) > 0) { required.push('previousWeeklyWalking') }
				if (parseInt(this.previousFrequencyBike) > 0) { required.push('previousWeeklyBike') }
				if (parseInt(this.previousFrequencyTransit) > 0) { required.push('previousWeeklyTransit') }
				if (parseInt(this.previousFrequencyMisc) > 0) { required.push('previousWeeklyMisc') }
			}

			if (this.stepIndex === 2) {
				if (this.usedResvana) {
					required = ['resvanaEffect', 'resvanaRecommend', 'moreWalking', 'moreBiking', 'moreTransit', 'moreCar']
				}
			}

			this.errors = required.filter(key => {
				return ! this.engaged.includes(key) || (this.$data[key] instanceof Array && this.$data[key].length === 0)
			})

			if (this.stepIndex === 0) {
				// If weeklyCar > 0, then the drivmedel should add up
				if (this.weeklyCar > 0 && (parseInt(this.weeklyElectricity) + parseInt(this.weeklyGas) + parseInt(this.weeklyHybrid) != this.weeklyCar)) {
					alert('Summan av drivmedel måste bli ' + this.weeklyCar)
					this.errors.push('weeklyElectricity')
					this.errors.push('weeklyGas')
					this.errors.push('weeklyHybrid')
				}
			}

			if (this.stepIndex === 1) {
				// If weeklyCar > 0, then the drivmedel should add up
				if (this.previousWeeklyCar > 0 && (parseInt(this.previousWeeklyElectricity) + parseInt(this.previousWeeklyGas) + parseInt(this.previousWeeklyHybrid) != this.previousWeeklyCar)) {
					alert('Summan av drivmedel måste bli ' + this.previousWeeklyCar)
					this.errors.push('previousWeeklyElectricity')
					this.errors.push('previousWeeklyGas')
					this.errors.push('previousWeeklyHybrid')
				}
			}

			// Remove most items if the person didn't use the service
			if (this.usedResvana === 'Nej') {
				required = []
			}

			this.errors = required.filter(val => ! this.engaged.includes(val))

			return this.errors.length === 0
		},

		prevStep() {
			window.scrollTo(0, 0)
			this.stepIndex--
		},

		saveState() {
			axios.post(this.$apiUrl + '/api/survey/10', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1
			})
		},

		submitResponse() {
			if ( ! this.validate()) {
				alert('Var god och fyll i alla rödmarkerade fält')
				return
			}

			axios.post(this.$apiUrl + '/api/survey/10', {
				...this.$data,
				uuid: this.uuid,
				slug: this.slug,
				seconds_passed: performance ? parseInt(performance.now() / 1000) : null,
				completed_pages: this.stepIndex + 1,
				complete: true
			}).then(res => {
				this.$router.push({name: 'SurveyThanks', params: {slug: this.slug}})
            })
		}
	},

	computed: {
		slug() {
			// Cookie slug precedence
			return this.$route.params.slug || this.$getCookie('openEndUser')
		},

		endUser() {
			return this.$store.state.endUser
		},

		weeklySum() {
			return parseInt(this.weeklyWalking) + parseInt(this.weeklyBike) + parseInt(this.weeklyTransit) + parseInt(this.weeklyCar) + parseInt(this.weeklyMisc)
		},

		computedWeeklyAverage() {
			if (this.numberOfTrips == 0) {
				return 0
			}

			return (this.weeklySum / this.numberOfTrips).toFixed(0).replace('.', ',')
		},

		yearlyDistance() {
			return this.weeklySum * 52
		},

		previousWeeklySum() {
			return parseInt(this.previousWeeklyWalking) + parseInt(this.previousWeeklyBike) + parseInt(this.previousWeeklyTransit) + parseInt(this.previousWeeklyCar) + parseInt(this.previousWeeklyMisc)
		},

		previousComputedWeeklyAverage() {
			if (this.previousNumberOfTrips == 0) {
				return 0
			}

			return (this.previousWeeklySum / this.previousNumberOfTrips).toFixed(0).replace('.', ',')
		},

		previousYearlyDistance() {
			return this.previousWeeklySum * 52
		},

    apiUrl() {
      return this.$apiUrl
    },
	}
}
</script>
